<script setup lang="ts">
  // Ported from cartadigital-pos app/components/pos/order/PosOrderTopBar.vue.
  // Admin extras: covers chip (Seat), customer picker (ModalsCustomers) and
  // delivery address via ModalChooseAddress (map + geocoding reuse).
  import { computed, getCurrentInstance, ref, watch } from 'vue';
  import { useElementSize, useWindowSize } from '@vueuse/core';
  import { OrderType } from '@/enums/OrderEnums';
  import { AlertType } from '@/interfaces/IAlert';
  import { IClubMemberIdentification } from '@/interfaces/IClubMember';
  import { ICustomer } from '@/interfaces/ICustomer';
  import { IDeliveryInfo } from '@/interfaces/IDeliveryInfo';
  import {
    clubProgramCoversBranch,
    valueForPoints,
  } from '@/domain/club/club-program-math';
  import {
    getTopBarMode,
    TOP_BAR_CLUB_CHIP_COMPACT_BELOW,
    TOP_BAR_CLUB_CHIP_ICONLESS_BELOW,
    TTopBarClubSlot,
  } from '@/domain/pos/pos-compaction';
  import { POS_ORDER_TYPE_UI_OPTIONS } from '@/domain/pos/order-type-ui';
  import {
    isOrderTypeEnabled,
    resolveAllowedOrderType,
  } from '@/domain/pos/order-type-policies';
  import { useModal } from '@/composition/use-modal';
  import { usePosMoney } from '@/composition/use-pos-money';
  import { useUi } from '@/composition/use-ui';
  import {
    isEmptyUneditedDraft,
    usePosDraftsStore,
  } from '@/stores/pos-drafts.store';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import ModalChooseAddress from '@/components/modals/ModalChooseAddress.vue';
  import ModalsCustomers from '@/components/modals/customer/ModalsCustomers.vue';
  import V2ModalClubAttach from '@/components/modals/club/V2ModalClubAttach.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiListbox from '@/components/pos-v2/ui/PosUiListbox.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import PosV2CoversModal, {
    IPosV2CoversResult,
  } from '@/components/pos-v2/modals/PosV2CoversModal.vue';

  const posDrafts = usePosDraftsStore();
  const restaurantStore = useRestaurantStore();
  const modals = useModal();
  const ui = useUi();
  const { formatComputedMoney } = usePosMoney();

  // vue-i18n in script (Vue 2.7): translate through the component proxy.
  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const draft = computed(() => posDrafts.activeDraft);
  const orderType = computed(() => draft.value?.type ?? OrderType.Seat);

  //#region Adaptive width (short/narrow desktops)

  const { width: winWidth } = useWindowSize();
  const topBarInnerRef = ref<HTMLElement>();
  const { width: barWidth } = useElementSize(topBarInnerRef);

  // clubSlot is declared in the club region below; computeds evaluate lazily
  // (first template render), so the forward reference is safe.
  const barMode = computed(() =>
    getTopBarMode(
      winWidth.value,
      barWidth.value,
      orderType.value,
      clubSlot.value
    )
  );

  // Away/Delivery require a customer name (store validation rule).
  const nameRequired = computed(() => orderType.value !== OrderType.Seat);

  // Trigger status dot: red = required name missing; emerald = any collapsed
  // value filled; null = nothing to signal.
  const collapsedDotState = computed<'error' | 'filled' | null>(() => {
    if (nameRequired.value && !customerName.value.trim()) return 'error';
    const values = [customerName.value];
    if (orderType.value !== OrderType.Seat) values.push(customerPhone.value);
    if (orderType.value === OrderType.Delivery) {
      values.push(deliveryNotes.value);
    }
    return values.some((v) => v.trim()) ? 'filled' : null;
  });

  //#endregion

  const branchFlags = computed(() => ({
    tableOrders: restaurantStore.selectedBranch?.tableOrders ?? null,
    takeAway: restaurantStore.selectedBranch?.takeAway ?? null,
    delivery: restaurantStore.selectedBranch?.delivery ?? null,
  }));

  //#region Order type switching

  const isTypeSwitchLocked = computed(() => draft.value?.mode !== 'create');

  function isTypeOptionDisabled(type: OrderType): boolean {
    if (isTypeSwitchLocked.value) return true;
    return !isOrderTypeEnabled(branchFlags.value, type);
  }

  function switchOrderType(type: OrderType) {
    const d = draft.value;
    if (!d || d.type === type || isTypeOptionDisabled(type)) return;
    posDrafts.updateDraft(d.id, { type });
  }

  // Auto-retarget empty drafts whose type got disabled; warn once otherwise.
  const warnedDraftIds = new Set<string>();

  watch(
    () => ({
      draftId: draft.value?.id ?? null,
      mode: draft.value?.mode ?? null,
      type: draft.value?.type ?? null,
      ...branchFlags.value,
    }),
    () => {
      const d = draft.value;
      if (!d || d.mode !== 'create') return;
      if (isOrderTypeEnabled(branchFlags.value, d.type)) return;

      const nextType = resolveAllowedOrderType(branchFlags.value, d.type);
      if (!nextType) {
        if (!warnedDraftIds.has(d.id)) {
          warnedDraftIds.add(d.id);
          ui.showToast(AlertType.Info, i18nT('posV2.flowDisabled'));
        }
        return;
      }

      if (isEmptyUneditedDraft(d)) {
        if (nextType !== d.type) {
          posDrafts.updateDraft(d.id, { type: nextType, isDirty: false });
        }
        return;
      }

      if (!warnedDraftIds.has(d.id)) {
        warnedDraftIds.add(d.id);
        ui.showToast(AlertType.Info, i18nT('posV2.typeDisabledSwitch'));
      }
    },
    { immediate: true }
  );

  //#endregion

  //#region Metadata

  const tableItems = computed(() =>
    (restaurantStore.tables ?? [])
      .filter((t) => t.enabled)
      .map((t) => ({ id: t.id, label: t.name }))
  );

  function setTable(value: number | string | null) {
    const d = draft.value;
    if (!d) return;
    posDrafts.updateDraft(d.id, {
      tableId: typeof value === 'number' ? value : undefined,
    });
  }

  const customerName = computed({
    get: () => draft.value?.customerData.name ?? '',
    set: (value: string) => {
      const d = draft.value;
      if (!d) return;
      posDrafts.updateDraft(d.id, {
        customerData: { ...d.customerData, name: value },
      });
    },
  });

  const customerPhone = computed({
    get: () => draft.value?.customerData.phone ?? '',
    set: (value: string) => {
      const d = draft.value;
      if (!d) return;
      posDrafts.updateDraft(d.id, {
        customerData: { ...d.customerData, phone: value },
      });
    },
  });

  const deliveryAddressLabel = computed(() => {
    const info = draft.value?.deliveryInfo;
    if (!info?.address) return null;
    return [info.address, info.address2].filter(Boolean).join(', ');
  });

  const deliveryNotes = computed({
    get: () => draft.value?.deliveryInfo?.addressComment ?? '',
    set: (value: string) => {
      const d = draft.value;
      if (!d) return;
      posDrafts.updateDraft(d.id, {
        deliveryInfo: {
          address: d.deliveryInfo?.address ?? '',
          address2: d.deliveryInfo?.address2 ?? '',
          addressComment: value,
          lat: d.deliveryInfo?.lat,
          lng: d.deliveryInfo?.lng,
        },
      });
    },
  });

  const coversLabel = computed(() => {
    const covers = draft.value?.covers;
    return covers != null ? `${covers}` : null;
  });

  //#endregion

  //#region Modals (covers / customer / address)

  async function openCoversModal() {
    const d = draft.value;
    if (!d || d.type !== OrderType.Seat) return;
    const draftId = d.id;

    const table = d.tableId
      ? restaurantStore.tables?.find((t) => t.id === d.tableId)
      : null;

    const result = await modals.showDynamicDialog<IPosV2CoversResult | null>(
      PosV2CoversModal,
      {
        tableName: table?.name ?? null,
        initial: d.covers,
        skipLabel: i18nT('posV2.removeCovers'),
      },
      'pos-covers',
      { width: 420 }
    );

    if (!result) return;
    if (!posDrafts.drafts.find((dr) => dr.id === draftId)) return;
    posDrafts.updateDraft(draftId, { covers: result.covers });
  }

  function openCustomersModal() {
    const draftId = draft.value?.id;
    if (!draftId) return;

    modals.show(
      // @ts-ignore vue-js-modal accepts SFC components loosely
      ModalsCustomers,
      {},
      {
        name: 'customers',
        adaptive: true,
        classes: 'dialog--height-limit',
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 600,
        clickToClose: true,
      },
      {
        chooseCustomer: (customer: ICustomer) => {
          posDrafts.applyCustomer(draftId, customer);
          modals.hide('customers');
        },
        close: () => modals.hide('customers'),
      }
    );
  }

  //#region Club member attach

  // Program presence comes from the /full payload (active programs only);
  // the button hides at branches the program doesn't cover.
  const clubAvailable = computed(() => {
    const program = restaurantStore.restaurant?.club;
    if (!program) return false;
    return clubProgramCoversBranch(
      program,
      restaurantStore.selectedBranch?.id ?? null
    );
  });

  interface IClubChip {
    name: string;
    points: number;
    /** Money value of the points; 0 when below the minimum or redeem is off. */
    pointsValue: number;
    /** Wallet balance; null while unknown (member hydrated from an order)
     *  or when the program has no wallet (the segment is then hidden). */
    cash: number | null;
    /** At least one resource can be spent on this order. */
    usable: boolean;
  }

  const clubChip = computed<IClubChip | null>(() => {
    const d = draft.value;
    if (!d?.clubMemberId) return null;
    const program = restaurantStore.restaurant?.club;
    const points = d.clubMemberPoints ?? 0;
    const cash = program?.balanceEnabled ? d.clubMemberCash ?? null : null;
    const redeemable =
      !!program &&
      program.pointsEnabled &&
      points >= Math.max(program.minRedeemPoints, 1);
    const pointsValue =
      redeemable && program ? valueForPoints(program, points) : 0;
    const cashUsable = (cash ?? 0) > 0;
    return {
      name: d.clubMemberName ?? i18nT('posV2.clubMember'),
      points,
      pointsValue,
      cash,
      usable: pointsValue > 0 || cashUsable,
    };
  });

  // Squeezed towards its 144px minimum, "Puntos $3.500 | Saldo $8.000" would
  // truncate to "Puntos $ … | Saldo $ …". Below the measured thresholds the
  // chip first drops the words, then its icon, and keeps the two figures
  // (colors + tooltip still say which is which). The chip's width comes from
  // the flex layout alone (definite basis, zero intrinsic width), so swapping
  // the content never feeds back into the measurement.
  const clubChipRef = ref<HTMLElement>();
  const { width: clubChipWidth } = useElementSize(clubChipRef);
  const clubChipCompact = computed(
    () =>
      clubChipWidth.value > 0 &&
      clubChipWidth.value < TOP_BAR_CLUB_CHIP_COMPACT_BELOW
  );
  const clubChipIconless = computed(
    () =>
      clubChipWidth.value > 0 &&
      clubChipWidth.value < TOP_BAR_CLUB_CHIP_ICONLESS_BELOW
  );

  function clubChipPointsLabel(chip: IClubChip, compact: boolean): string {
    if (chip.pointsValue <= 0) {
      return `${chip.points} ${i18nT('posV2.clubPointsSuffix')}`;
    }
    const value = formatComputedMoney(chip.pointsValue);
    return compact ? value : i18nT('posV2.clubChipPoints', { value });
  }

  function clubChipBalanceLabel(
    chip: IClubChip,
    compact: boolean
  ): string | null {
    if (chip.cash == null) return null;
    if (chip.cash <= 0) return i18nT('posV2.clubNoBalance');
    const value = formatComputedMoney(chip.cash);
    return compact ? value : i18nT('posV2.clubChipBalance', { value });
  }

  const clubChipPointsText = computed(() =>
    clubChip.value
      ? clubChipPointsLabel(clubChip.value, clubChipCompact.value)
      : ''
  );

  const clubChipBalanceText = computed(() =>
    clubChip.value
      ? clubChipBalanceLabel(clubChip.value, clubChipCompact.value)
      : null
  );

  /** Always the full wording, so the tooltip explains the compact figures. */
  const clubChipTitle = computed(() => {
    const chip = clubChip.value;
    if (!chip) return '';
    return [
      chip.name,
      clubChipPointsLabel(chip, false),
      clubChipBalanceLabel(chip, false),
    ]
      .filter(Boolean)
      .join(' · ');
  });

  /** Feeds the adaptive bar mode: the club slot widens the minimum layout. */
  const clubSlot = computed<TTopBarClubSlot>(() => {
    if (!clubAvailable.value) return 'none';
    return clubChip.value ? 'chip' : 'button';
  });

  async function openClubAttachModal() {
    const draftId = draft.value?.id;
    if (!draftId) return;

    const result =
      await modals.showDynamicDialog<IClubMemberIdentification | null>(
        V2ModalClubAttach,
        {},
        'club-attach',
        { width: 480 }
      );

    if (!result) return;
    if (!posDrafts.drafts.find((dr) => dr.id === draftId)) return;
    posDrafts.applyClubMember(draftId, result.member, result.code);
  }

  function detachClubMember() {
    const draftId = draft.value?.id;
    if (!draftId) return;
    posDrafts.applyClubMember(draftId, null, null);
  }

  //#endregion

  function openAddressModal() {
    const d = draft.value;
    if (!d) return;
    const draftId = d.id;

    modals.show(
      // @ts-ignore vue-js-modal accepts SFC components loosely
      ModalChooseAddress,
      {
        deliveryInfo: d.deliveryInfo ?? undefined,
      },
      {
        name: 'choose-address',
        adaptive: true,
        classes: 'dialog--height-limit',
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 600,
        clickToClose: true,
      },
      {
        save: (place: IDeliveryInfo) => {
          if (posDrafts.drafts.find((dr) => dr.id === draftId)) {
            posDrafts.updateDraft(draftId, { deliveryInfo: place });
          }
          modals.hide('choose-address');
        },
      }
    );
  }

  //#endregion
</script>

<template>
  <div v-if="draft" class="pos-v2-top-bar">
    <div ref="topBarInnerRef" class="top-bar-inner">
      <!-- Order type selector: on narrow bars only the active type keeps
           its label; inactive types become icon-only. -->
      <div class="type-selector">
        <button
          v-for="option in POS_ORDER_TYPE_UI_OPTIONS"
          :key="option.value"
          type="button"
          class="type-button"
          :class="{
            active: orderType === option.value,
            'is-icon-only':
              orderType !== option.value && barMode !== 'full',
          }"
          :disabled="isTypeOptionDisabled(option.value)"
          @click="switchOrderType(option.value)"
        >
          <PosUiIcon :name="option.icon" class="type-icon" />
          <!-- full: all labels; no-labels: active only; collapsed: none -->
          <span
            v-show="
              barMode === 'full' ||
              (orderType === option.value && barMode !== 'collapsed')
            "
            class="type-label"
            >{{ option.label }}</span
          >
        </button>
      </div>

      <!-- Metadata inputs -->
      <div class="metadata">
        <!-- Seat -->
        <template v-if="orderType === OrderType.Seat">
          <PosUiListbox
            :value="draft.tableId ?? null"
            :items="tableItems"
            :searchable="tableItems.length > 10"
            :clearable="true"
            :placeholder="$t('posV2.selectTable')"
            class="metadata-input"
            @input="setTable"
          />
          <PosUiInput
            v-if="barMode !== 'collapsed'"
            :value="customerName"
            :placeholder="$t('posV2.nameOptionalPlaceholder')"
            class="metadata-input"
            @input="(v) => (customerName = v)"
          />
          <button
            type="button"
            class="covers-chip"
            :title="$t('posV2.covers')"
            @click="openCoversModal"
          >
            <PosUiIcon name="account-group" class="covers-icon" />
            <!-- Collapsed with no value yet: icon-only (the placeholder word
                 is the widest part of the chip and the bar is at its
                 tightest). A set value is short and always shown. -->
            <span
              v-show="coversLabel || barMode !== 'collapsed'"
              class="covers-value"
            >
              {{ coversLabel ?? $t('posV2.covers') }}
            </span>
          </button>
        </template>

        <!-- Away -->
        <template v-if="orderType === OrderType.Away">
          <template v-if="barMode !== 'collapsed'">
            <PosUiInput
              :value="customerName"
              :placeholder="$t('posV2.nameRequiredPlaceholder')"
              class="metadata-input"
              @input="(v) => (customerName = v)"
            />
            <PosUiInput
              :value="customerPhone"
              :placeholder="$t('posV2.phoneOptionalPlaceholder')"
              type="tel"
              class="metadata-input"
              @input="(v) => (customerPhone = v)"
            />
          </template>
        </template>

        <!-- Delivery -->
        <template v-if="orderType === OrderType.Delivery">
          <template v-if="barMode !== 'collapsed'">
            <PosUiInput
              :value="customerName"
              :placeholder="$t('posV2.nameRequiredPlaceholder')"
              class="metadata-input"
              @input="(v) => (customerName = v)"
            />
            <PosUiInput
              :value="customerPhone"
              :placeholder="$t('posV2.phoneOptionalPlaceholder')"
              type="tel"
              class="metadata-input"
              @input="(v) => (customerPhone = v)"
            />
          </template>
          <button
            type="button"
            class="address-chip"
            :class="{ 'is-placeholder': !deliveryAddressLabel }"
            @click="openAddressModal"
          >
            <PosUiIcon name="map-marker" class="address-icon" />
            <span class="address-text">
              {{
                deliveryAddressLabel ?? $t('posV2.addressRequiredPlaceholder')
              }}
            </span>
          </button>
          <PosUiInput
            v-if="barMode !== 'collapsed'"
            :value="deliveryNotes"
            :placeholder="$t('posV2.deliveryNotesPlaceholder')"
            class="metadata-input"
            :maxlength="140"
            @input="(v) => (deliveryNotes = v)"
          />
        </template>

        <!-- Collapsed free-text inputs: one button + drop-down popover.
             The same store-backed computeds power both renderings. -->
        <PosUiPopover
          v-if="barMode === 'collapsed'"
          placement="bottom"
          align="end"
          anchor="trigger"
          class="customer-data-popover"
        >
          <template #trigger="{ toggle }">
            <PosUiButton
              icon="account-edit-outline"
              color="neutral"
              variant="outline"
              size="md"
              class="customer-data-button"
              @click="toggle"
            />
            <span
              v-if="collapsedDotState"
              class="customer-data-dot"
              :class="`is-${collapsedDotState}`"
            />
          </template>
          <div class="customer-data-body">
            <div class="popover-title">{{ $t('posV2.customerData') }}</div>
            <label class="popover-field">
              <span class="popover-label">
                {{ $t('posV2.metaCustomer') }}
                <span v-if="nameRequired" class="required-mark">*</span>
              </span>
              <PosUiInput
                :value="customerName"
                :placeholder="
                  nameRequired
                    ? $t('posV2.nameRequiredPlaceholder')
                    : $t('posV2.nameOptionalPlaceholder')
                "
                @input="(v) => (customerName = v)"
              />
            </label>
            <label v-if="orderType !== OrderType.Seat" class="popover-field">
              <span class="popover-label">{{ $t('posV2.metaPhone') }}</span>
              <PosUiInput
                :value="customerPhone"
                :placeholder="$t('posV2.phoneOptionalPlaceholder')"
                type="tel"
                @input="(v) => (customerPhone = v)"
              />
            </label>
            <label
              v-if="orderType === OrderType.Delivery"
              class="popover-field"
            >
              <span class="popover-label">{{ $t('posV2.deliveryNotes') }}</span>
              <PosUiInput
                :value="deliveryNotes"
                :placeholder="$t('posV2.deliveryNotesPlaceholder')"
                :maxlength="140"
                @input="(v) => (deliveryNotes = v)"
              />
            </label>
          </div>
        </PosUiPopover>

        <!-- Customer picker -->
        <PosUiButton
          icon="account-search"
          color="neutral"
          variant="outline"
          size="md"
          class="customer-button"
          @click="openCustomersModal"
        />

        <!-- Club member (attach button / attached chip) -->
        <template v-if="clubAvailable">
          <!-- Re-mounts on every attach (chip <-> button), which restarts
               the CSS pulse without any JS. -->
          <div
            v-if="clubChip"
            ref="clubChipRef"
            class="club-chip"
            :class="{
              'club-chip--usable': clubChip.usable,
              'club-chip--iconless': clubChipIconless,
            }"
            :title="clubChipTitle"
          >
            <PosUiIcon
              v-if="!clubChipIconless"
              name="star-circle"
              class="club-chip-icon"
            />
            <span class="club-chip-text">
              <span class="club-chip-name">{{ clubChip.name }}</span>
              <span class="club-chip-figures">
                <span class="club-chip-points">{{ clubChipPointsText }}</span>
                <template v-if="clubChipBalanceText">
                  <span class="club-chip-separator">|</span>
                  <span
                    class="club-chip-balance"
                    :class="{ 'is-empty': !clubChip.cash }"
                    >{{ clubChipBalanceText }}</span
                  >
                </template>
              </span>
            </span>
            <button
              type="button"
              class="club-chip-remove"
              :aria-label="$t('posV2.clubDetach')"
              @click="detachClubMember"
            >
              <PosUiIcon name="close" />
            </button>
          </div>
          <PosUiButton
            v-else
            icon="star-circle-outline"
            color="neutral"
            variant="outline"
            size="md"
            class="club-button"
            :title="$t('posV2.clubAttachTitle')"
            @click="openClubAttachModal"
          >
            {{ $t('posV2.metaClub') }}
          </PosUiButton>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .pos-v2-top-bar {
    @apply tw-sticky tw-top-0 tw-z-20 tw-border-b tw-border-neutral-200 tw-bg-white;

    .top-bar-inner {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-2 tw-py-2 lg:tw-flex-nowrap;
    }

    // Segmented control, same visual language as the main page mode tabs.
    .type-selector {
      @apply tw-flex tw-flex-shrink-0 tw-gap-0.5 tw-rounded-lg tw-bg-neutral-100 tw-p-0.5;
    }

    .type-button {
      @apply tw-flex tw-min-h-[44px] tw-min-w-[80px] tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-md tw-border tw-border-solid tw-border-transparent tw-bg-transparent tw-px-3 tw-py-2 tw-text-neutral-600 tw-transition-colors tw-duration-150 hover:tw-text-neutral-900;

      &.is-icon-only {
        @apply tw-min-w-0;
      }

      &:disabled {
        @apply tw-cursor-not-allowed tw-opacity-40 hover:tw-text-neutral-600;
      }

      &.active {
        @apply tw-border-neutral-200 tw-bg-white tw-text-neutral-900 tw-shadow-sm;
      }

      &.active:disabled {
        @apply tw-opacity-80;
      }

      .type-icon {
        @apply tw-text-lg;
      }

      .type-label {
        @apply tw-text-xs tw-font-medium md:tw-text-sm;
      }
    }

    .metadata {
      @apply tw-flex tw-w-full tw-flex-1 tw-flex-wrap tw-items-center tw-gap-2 lg:tw-w-auto lg:tw-flex-nowrap;

      .metadata-input {
        /* w-0: the inputs' intrinsic contribution to the bar is exactly
           their 150px minimum (what the mode thresholds are measured
           against); with an auto width the listbox placeholder + chevron
           would contribute ~154px and push the bar into overflow. */
        @apply tw-w-0 tw-min-w-[150px] tw-flex-1;
      }
    }

    .covers-chip {
      @apply tw-flex tw-h-11 tw-flex-shrink-0 tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-px-3 tw-text-neutral-700 tw-transition-colors hover:tw-bg-neutral-100;

      .covers-icon {
        @apply tw-text-lg tw-text-neutral-400;
      }

      .covers-value {
        @apply tw-text-sm tw-font-medium;
      }
    }

    .address-chip {
      @apply tw-flex tw-h-11 tw-min-w-[150px] tw-flex-1 tw-items-center tw-gap-1.5 tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-px-3 tw-text-left tw-text-neutral-700 tw-transition-colors hover:tw-bg-neutral-100;

      &.is-placeholder .address-text {
        @apply tw-text-neutral-400;
      }

      .address-icon {
        @apply tw-flex-shrink-0 tw-text-lg tw-text-neutral-400;
      }

      .address-text {
        @apply tw-truncate tw-text-sm;
      }
    }

    .customer-button,
    .club-button {
      @apply tw-flex-shrink-0;
    }

    .club-chip {
      /* flex 0 1 280px: claims its 280px before the flex-1 inputs grow (a
         plain flex-1 would split the free space evenly with them and leave
         the two-line text truncated even on wide bars), and shrinks with
         truncating text down to 144px so an attached member never forces
         the bar wider than TOP_BAR_CLUB_SLOT_EXTRA.chip assumes. The
         definite zero width keeps the nowrap text out of the bar's
         intrinsic min-content — with an auto width the full text would
         inflate it and defeat shrinking. Neutral outline by default:
         nothing to spend on this order. */
      @apply tw-flex tw-h-11 tw-w-0 tw-min-w-[144px] tw-flex-[0_1_280px] tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-px-2.5 tw-text-neutral-700;

      &--iconless {
        @apply tw-px-2;
      }

      .club-chip-icon {
        @apply tw-flex-shrink-0 tw-text-lg tw-text-neutral-400;
      }

      .club-chip-text {
        /* min-w-0: without it the nowrap text sets the chip's min-content
           and the chip can never shrink below the full text. */
        @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-leading-tight;
      }

      .club-chip-name {
        @apply tw-truncate tw-text-xs tw-font-medium;
      }

      .club-chip-figures {
        @apply tw-flex tw-min-w-0 tw-items-center tw-gap-1 tw-whitespace-nowrap tw-text-[12px] tw-tabular-nums tw-text-neutral-500;

        .club-chip-points,
        .club-chip-balance {
          @apply tw-truncate;
        }

        .club-chip-separator {
          @apply tw-flex-shrink-0 tw-text-neutral-300;
        }
      }

      .club-chip-remove {
        @apply tw-flex tw-h-6 tw-w-6 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-text-neutral-400 tw-transition-colors hover:tw-bg-neutral-100 hover:tw-text-neutral-800;
      }

      &--usable {
        @apply tw-border-orange-300 tw-bg-orange-50 tw-text-orange-900;
        animation: club-pulse 1.4s ease-out 2;

        .club-chip-icon {
          @apply tw-text-orange-600;
        }

        .club-chip-figures {
          @apply tw-font-bold tw-text-orange-900;

          .club-chip-balance {
            @apply tw-text-emerald-800;

            &.is-empty {
              @apply tw-font-medium tw-text-neutral-500;
            }
          }

          .club-chip-separator {
            @apply tw-text-orange-300;
          }
        }

        .club-chip-remove {
          @apply tw-text-orange-500 hover:tw-bg-orange-100 hover:tw-text-orange-800;
        }
      }
    }

    .customer-data-popover {
      @apply tw-flex-shrink-0;

      .customer-data-dot {
        @apply tw-pointer-events-none tw-absolute -tw-right-1 -tw-top-1 tw-h-2.5 tw-w-2.5 tw-rounded-full tw-border tw-border-white;

        &.is-error {
          @apply tw-bg-red-500;
        }

        &.is-filled {
          @apply tw-bg-emerald-500;
        }
      }

      .customer-data-body {
        @apply tw-flex tw-flex-col tw-gap-2;

        .popover-title {
          @apply tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-neutral-500;
        }

        .popover-field {
          @apply tw-flex tw-flex-col tw-gap-1;

          .popover-label {
            @apply tw-text-xs tw-font-medium tw-text-neutral-700;

            .required-mark {
              @apply tw-ml-0.5 tw-text-red-600;
            }
          }
        }
      }
    }
  }

  // Two soft rings on attach so the cashier notices spendable points/balance.
  @keyframes club-pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(234, 88, 12, 0.35);
    }
    100% {
      box-shadow: 0 0 0 8px rgba(234, 88, 12, 0);
    }
  }
</style>
